import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  User, 
  Mail, 
  Phone, 
  Camera, 
  Save, 
  Loader,
  ArrowLeft,
  Shield,
  Bell,
  Upload,
  X,
  Lock,
  Info,
  MapPin
} from 'lucide-react';
import Cropper from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import { supabase } from '../lib/supabase';
import { useNotification } from '../context/NotificationContext';
import FormError from '../components/FormError';
import AddressBook from '../components/AddressBook';
import { z } from 'zod';

const profileSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  fullName: z.string().min(2, 'Full name must be at least 2 characters long').optional(),
  phone: z.string().regex(/^\d{10}$/, 'Please enter a valid 10-digit phone number').optional(),
});

type ProfileFormData = z.infer<typeof profileSchema>;

function Profile() {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'general' | 'security' | 'notifications' | 'addresses'>('general');
  const [formData, setFormData] = useState<ProfileFormData>({
    email: '',
    fullName: '',
    phone: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof ProfileFormData, string>>>({});
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const cropperRef = useRef<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [notificationPreferences, setNotificationPreferences] = useState({
    booking_updates: true,
    payment_notifications: true,
    promotional_emails: false,
    service_reminders: true,
  });
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          navigate('/login');
          return;
        }

        setUserId(session.user.id);

        // Now fetch the profile
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single();

        if (error) {
          console.error('Profile fetch error:', error);
          throw error;
        }

        setFormData({
          email: session.user.email || '',
          fullName: profile?.full_name || '',
          phone: profile?.phone || '',
        });

        if (profile?.avatar_url) {
          const { data: { publicUrl } } = supabase
            .storage
            .from('avatars')
            .getPublicUrl(profile.avatar_url);
          setAvatarUrl(publicUrl);
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
        showNotification('error', 'Failed to load profile data');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [navigate, showNotification]);

  const validateForm = () => {
    try {
      profileSchema.parse(formData);
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof Error) {
        const zodError = JSON.parse(error.message);
        const newErrors: typeof errors = {};
        zodError.forEach((err: any) => {
          const path = err.path[0] as keyof ProfileFormData;
          newErrors[path] = err.message;
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setUploadedImage(reader.result as string);
        setIsUploadModalOpen(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCropComplete = async () => {
    if (!cropperRef.current) return;

    try {
      const canvas = cropperRef.current.cropper.getCroppedCanvas();
      const blob = await new Promise<Blob>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session');

      // Create a unique filename
      const timestamp = Date.now();
      const filePath = `${session.user.id}/avatar-${timestamp}.jpg`;

      // Upload the file
      const { data, error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, blob, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) throw uploadError;

      // Update profile with new avatar URL
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ 
          avatar_url: data.path,
          updated_at: new Date().toISOString()
        })
        .eq('id', session.user.id);

      if (updateError) throw updateError;

      // Get the public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(data.path);

      setAvatarUrl(publicUrl);
      setIsUploadModalOpen(false);
      showNotification('success', 'Profile picture updated successfully');
    } catch (error) {
      console.error('Error uploading image:', error);
      showNotification('error', 'Failed to upload profile picture. Please try again.');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      showNotification('error', 'Please fix the form errors before saving');
      return;
    }

    setSaving(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session found');

      const { error: upsertError } = await supabase
        .from('profiles')
        .upsert({
          id: session.user.id,
          full_name: formData.fullName,
          phone: formData.phone,
          updated_at: new Date().toISOString(),
        });

      if (upsertError) throw upsertError;

      showNotification('success', 'Profile updated successfully');
    } catch (error) {
      console.error('Error updating profile:', error);
      showNotification('error', 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const tabs = [
    { id: 'general', label: 'General', icon: User },
    { id: 'addresses', label: 'Addresses', icon: MapPin },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'notifications', label: 'Notifications', icon: Bell },
  ] as const;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate('/dashboard')}
            className="mb-6 text-gray-600 hover:text-gray-900 transition-colors flex items-center gap-2 group"
          >
            <ArrowLeft className="w-4 h-4 transform group-hover:-translate-x-1 transition-transform" />
            Back to Dashboard
          </button>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
            {/* Profile Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-8 text-white">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="relative group">
                  <div className="w-24 h-24 rounded-full bg-white/10 backdrop-blur-sm ring-4 ring-white/20 overflow-hidden">
                    {avatarUrl ? (
                      <img 
                        src={avatarUrl} 
                        alt="Profile" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <User className="w-12 h-12" />
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute bottom-0 right-0 p-2 bg-blue-500 rounded-full hover:bg-blue-600 transition-colors"
                  >
                    <Camera className="w-4 h-4" />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </div>
                <div>
                  <h1 className="text-3xl font-bold">{formData.fullName || 'Your Profile'}</h1>
                  <p className="text-blue-100">{formData.email}</p>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="border-b dark:border-gray-700">
              <div className="flex">
                {tabs.map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    onClick={() => setActiveTab(id)}
                    className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors relative ${
                      activeTab === id
                        ? 'text-blue-600 dark:text-blue-400'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                    {activeTab === id && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-6">
              {activeTab === 'general' && (
                <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl">
                  <div className="grid gap-6 md:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Full Name
                      </label>
                      <div className="relative group">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors w-5 h-5" />
                        <input
                          type="text"
                          value={formData.fullName}
                          onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                          className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                            errors.fullName ? 'border-red-300' : 'border-gray-300'
                          }`}
                          placeholder="Enter your full name"
                        />
                      </div>
                      {errors.fullName && <FormError message={errors.fullName} />}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Phone Number
                      </label>
                      <div className="relative group">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors w-5 h-5" />
                        <input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '') })}
                          className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                            errors.phone ? 'border-red-300' : 'border-gray-300'
                          }`}
                          placeholder="Enter your phone number"
                          maxLength={10}
                        />
                      </div>
                      {errors.phone && <FormError message={errors.phone} />}
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Email Address
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                          type="email"
                          value={formData.email}
                          disabled
                          className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                        />
                      </div>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Contact support to change your email address
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-4 pt-4">
                    <button
                      type="submit"
                      disabled={saving}
                      className="bg-blue-600 text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed group"
                    >
                      {saving ? (
                        <>
                          <Loader className="w-5 h-5 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-5 h-5 group-hover:scale-110 transition-transform" />
                          Save Changes
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}

              {activeTab === 'addresses' && userId && (
                <AddressBook userId={userId} />
              )}

              {activeTab === 'security' && (
                <div className="max-w-2xl">
                  <div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-4 mb-6">
                    <div className="flex items-start gap-4">
                      <Shield className="w-6 h-6 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                      <div>
                        <h3 className="font-medium dark:text-white">Account Security</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-300">Manage your password and security settings</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 border dark:border-gray-600 rounded-lg">
                      <div className="flex items-center gap-4">
                        <Lock className="w-5 h-5 text-gray-400" />
                        <div>
                          <h4 className="font-medium dark:text-white">Password</h4>
                          <p className="text-sm text-gray-600 dark:text-gray-300">Change your password</p>
                        </div>
                      </div>
                      <button
                        onClick={() => navigate('/reset-password')}
                        className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium text-sm"
                      >
                        Update
                      </button>
                    </div>

                    <div className="flex items-center justify-between p-4 border dark:border-gray-600 rounded-lg">
                      <div className="flex items-center gap-4">
                        <Mail className="w-5 h-5 text-gray-400" />
                        <div>
                          <h4 className="font-medium dark:text-white">Email Verification</h4>
                          <p className="text-sm text-gray-600 dark:text-gray-300">Verify your email address</p>
                        </div>
                      </div>
                      <span className="px-3 py-1 bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400 text-sm font-medium rounded-full">
                        Verified
                      </span>
                    </div>

                    <div className="flex items-center justify-between p-4 border dark:border-gray-600 rounded-lg">
                      <div className="flex items-center gap-4">
                        <Phone className="w-5 h-5 text-gray-400" />
                        <div>
                          <h4 className="font-medium dark:text-white">Phone Verification</h4>
                          <p className="text-sm text-gray-600 dark:text-gray-300">Verify your phone number</p>
                        </div>
                      </div>
                      <button
                        onClick={() => showNotification('info', 'Phone verification coming soon!')}
                        className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium text-sm"
                      >
                        Verify
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'notifications' && (
                <div className="max-w-2xl">
                  <div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-4 mb-6">
                    <div className="flex items-start gap-4">
                      <Bell className="w-6 h-6 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                      <div>
                        <h3 className="font-medium dark:text-white">Notification Preferences</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-300">Manage how you receive notifications</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {[
                      { key: 'booking_updates', label: 'Booking Updates', description: 'Get notified about your booking status' },
                      { key: 'payment_notifications', label: 'Payment Notifications', description: 'Receive payment confirmations and receipts' },
                      { key: 'promotional_emails', label: 'Promotional Emails', description: 'Stay informed about special offers and discounts' },
                      { key: 'service_reminders', label: 'Service Reminders', description: 'Get reminders before your scheduled service' },
                    ].map(({ key, label, description }) => (
                      <div key={key} className="flex items-center justify-between p-4 border dark:border-gray-600 rounded-lg">
                        <div>
                          <h4 className="font-medium dark:text-white">{label}</h4>
                          <p className="text-sm text-gray-600 dark:text-gray-300">{description}</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={notificationPreferences[key as keyof typeof notificationPreferences]}
                            onChange={(e) => {
                              setNotificationPreferences(prev => ({
                                ...prev,
                                [key]: e.target.checked
                              }));
                              showNotification('success', 'Notification preferences updated');
                            }}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-blue-600 dark:text-blue-400">
                        You can also manage notification settings for specific services in your dashboard.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Image Upload Modal */}
              {isUploadModalOpen && uploadedImage && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-2xl w-full mx-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold">Crop Profile Picture</h3>
                      <button
                        onClick={() => setIsUploadModalOpen(false)}
                        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                      >
                        <X className="w-6 h-6" />
                      </button>
                    </div>
                    <div className="mb-6">
                      <Cropper
                        ref={cropperRef}
                        src={uploadedImage}
                        style={{ height: 400, width: '100%' }}
                        aspectRatio={1}
                        guides={true}
                        viewMode={1}
                        dragMode="move"
                        scalable={true}
                        cropBoxMovable={true}
                        cropBoxResizable={true}
                        highlight={false}
                        toggleDragModeOnDblclick={false}
                      />
                    </div>
                    <div className="flex justify-end gap-3">
                      <button
                        onClick={() => setIsUploadModalOpen(false)}
                        className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleCropComplete}
                        className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center gap-2"
                      >
                        <Upload className="w-5 h-5" />
                        Upload
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Profile;